import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, ZoomIn, ZoomOut } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'

/** 拡大時の幅 (画面幅に対する倍率)。2.5 倍あれば、横長バナーの小さい注記まで読める。 */
const ZOOM_SCALE = 2.5

/** これより横長なら**開いた瞬間から拡大**する。縦画面に横長画像を収めると
 *  カードに出ているのと同じ大きさにしかならず、開いた意味が無いため。 */
const WIDE_ASPECT = 1.6

/**
 * 画像を全画面で見せるビューア (#1886)。
 *
 * バナーは**文字が焼き込まれたデザイン**で、430px 幅のカードに収めると小さい文字が
 * 読めない。かといって常時大きく出すと画面が画像で埋まる。読みたい人だけが開く形にする。
 *
 * **body 直下に出す (createPortal)。** `.animate-fade-in` は transform を
 * `forwards` で残すため、祖先が `position: fixed` の包含ブロックになり、
 * そのまま置くと**オーバーレイが 430px の列に閉じ込められる** (実際になった)。
 *
 * 拡大を自前で持つのは、index.html の viewport が `user-scalable=no` で
 * **ブラウザのピンチズームがアプリ全体で無効**なため。倍率が無いと開いても読めない。
 */
export default function ImageLightbox({ src, onClose }: { src: string; onClose: () => void }) {
  const { t } = useI18n()
  const [zoomed, setZoomed] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // 背面が動くと「閉じたときに元の位置を見失う」ので開いている間は固定する
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  /** 拡大したら中央へ寄せる。左端のままだと**端の余白や縁の柄**が映るだけで、
   *  読みたい中央の見出しが画面外に残る。 */
  const centerScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2)
    el.scrollTop = Math.max(0, (el.scrollHeight - el.clientHeight) / 2)
  }, [])

  useEffect(() => {
    if (zoomed) centerScroll()
  }, [zoomed, centerScroll])

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/95" role="dialog" aria-modal="true">
      {/* 何もない所をタップしたら閉じる。画像そのものは拡大の切り替えに使う。 */}
      <div
        ref={scrollRef}
        className="absolute inset-0 overflow-auto overscroll-contain"
        onClick={onClose}
      >
        <div className={zoomed ? 'flex min-h-full w-max items-center' : 'flex min-h-full items-center justify-center p-2'}>
          {/* 拡大中は幅を画面の 2.5 倍にして横スクロールで読ませる。
            * object-contain のまま伸ばしても画面幅に収まるだけで文字は大きくならない。 */}
          <img
            src={src}
            alt=""
            onLoad={(e) => {
              const el = e.currentTarget
              if (el.naturalHeight > 0 && el.naturalWidth / el.naturalHeight >= WIDE_ASPECT) {
                setZoomed(true)
              }
            }}
            onClick={(e) => { e.stopPropagation(); setZoomed((v) => !v) }}
            className={
              zoomed
                ? 'max-w-none cursor-zoom-out'
                : 'max-h-[90vh] w-full cursor-zoom-in object-contain'
            }
            style={zoomed ? { width: `${ZOOM_SCALE * 100}vw` } : undefined}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={() => setZoomed((v) => !v)}
        className="tap-effect absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-white/90 px-4 py-2 font-body text-sm text-text-primary shadow"
      >
        {zoomed ? <ZoomOut size={16} /> : <ZoomIn size={16} />}
        {t(zoomed ? 'stampRally.imageZoomOut' : 'stampRally.imageZoomIn')}
      </button>

      <button
        type="button"
        onClick={onClose}
        aria-label={t('common.close')}
        className="tap-effect absolute right-3 top-3 rounded-full bg-white/90 p-2 text-text-primary shadow"
      >
        <X size={20} />
      </button>
    </div>,
    document.body,
  )
}
